import { apiClient } from '../client';
import { API_ENDPOINTS } from '../config';

export interface Conversation {
  id: string;
  tenant_id: string;
  business_account_id: string;
  contact_id: string;
  status: 'active' | 'archived' | 'blocked';
  last_message_at: string | null;
  last_inbound_message_at?: string | null;
  agent_stepped_in_at?: string | null;
  assigned_agent_id?: string | null;
  created_at: string;
  updated_at: string;
  // Relations
  contact?: {
    id: string;
    name: string | null;
    phone_number: string;
    profile_picture_url: string | null;
    status: string;
    last_seen: string | null;
  };
  last_message?: {
    id: string;
    content_text: string | null;
    message_type: string;
    is_inbound: boolean;
    created_at: string;
    status: string;
  };
  unread_count?: number;
  scheduled_message_count?: number;
}

export interface ConversationFilters {
  business_account_id?: string;
  status?: string;
  contact_id?: string;
  search?: string;
  priority?: 'high' | 'medium' | 'low';
  isRequested?: boolean;
  isSteppedIn?: boolean;
  limit?: number;
  offset?: number;
  pageToken?: string;
  orderBy?: string;
  ascending?: boolean;
  startDate?: string;
  endDate?: string;
  hasScheduledMessages?: boolean;
  isUnread?: boolean;
  assigned_to?: string;
  /** Show only conversations of contacts the given campaign was actually SENT to. */
  campaignId?: string;
  /** Show only conversations of contacts carrying the given tag. */
  tagId?: string;
}

export interface CreateConversationData {
  contactId: string;
  businessAccountId: string;
  status?: string;
}

export const conversationsApi = {
  /**
   * Get all conversations with optional filtering
   */
  async getAll(filters: ConversationFilters = {}) {
    const params = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, String(value));
      }
    });

    const endpoint = `${API_ENDPOINTS.CONVERSATIONS}${params.toString() ? `?${params.toString()}` : ''}`;
    return apiClient.get<Conversation[]>(endpoint);
  },

  /**
   * Get a single conversation by ID
   */
  async getById(id: string) {
    return apiClient.get<Conversation>(`${API_ENDPOINTS.CONVERSATIONS}/${id}`);
  },

  /**
   * Get conversations by business account
   */
  async getByBusinessAccount(businessAccountId: string, filters: Omit<ConversationFilters, 'business_account_id'> = {}) {
    return this.getAll({ ...filters, business_account_id: businessAccountId });
  },

  /**
   * Get recent conversations
   */
  async getRecent(limit: number = 5, businessAccountId?: string) {
    return this.getAll({
      business_account_id: businessAccountId,
      limit,
      orderBy: 'last_message_at',
      ascending: false,
    });
  },

  /**
   * Get conversations that contain at least one starred message for the
   * current user, scoped to a business account.
   */
  async getStarred(businessAccountId?: string, limit = 200) {
    const qs = new URLSearchParams();
    if (businessAccountId) qs.append('business_account_id', businessAccountId);
    qs.append('limit', String(limit));
    return apiClient.get(`${API_ENDPOINTS.CONVERSATIONS}/starred?${qs.toString()}`);
  },

  /**
   * Returns aggregate badge counts for a business account in one index-scan.
   * No row data, no LIMIT — safe for any account size and RDS Data API.
   */
  async getUnreadSummary(businessAccountId: string) {
    return apiClient.get<{
      unreadConversationCount: number;
      unreadMessageCount: number;
      requestedCount: number;
      steppedInCount: number;
    }>(`${API_ENDPOINTS.CONVERSATIONS}/unread-summary?business_account_id=${encodeURIComponent(businessAccountId)}`);
  },

  /**
   * Create a new conversation
   */
  async create(data: CreateConversationData) {
    return apiClient.post<Conversation>(API_ENDPOINTS.CONVERSATIONS, data);
  },

  /**
   * Update conversation status
   */
  async updateStatus(id: string, status: Conversation['status']) {
    return apiClient.put<Conversation>(`${API_ENDPOINTS.CONVERSATIONS}/${id}`, { status });
  },

  /**
   * Archive conversation
   */
  async archive(id: string) {
    return this.updateStatus(id, 'archived');
  },

  /**
   * Unarchive conversation
   */
  async unarchive(id: string) {
    return this.updateStatus(id, 'active');
  },

  /**
   * Delete a conversation
   */
  async delete(id: string) {
    return apiClient.delete<{ success: boolean; message: string }>(`${API_ENDPOINTS.CONVERSATIONS}/${id}`);
  },

  /**
   * Mark conversation as stepped in by agent
   */
  async stepIn(id: string) {
    return apiClient.post<{ success: boolean; data: { id: string; agentSteppedInAt: string } }>(
      `${API_ENDPOINTS.CONVERSATIONS}/${id}/step-in`
    );
  },

  /**
   * Assign conversation to an agent
   */
  async assign(id: string, agentId: string | null | '') {
    return apiClient.post<{ success: boolean; data: { id: string; assignedAgentId: string | null } }>(
      `${API_ENDPOINTS.CONVERSATIONS}/${id}/assign`,
      { agentId: agentId || null }
    );
  },

  /**
   * Update conversation priority
   */
  async updatePriority(id: string, priority: 'high' | 'medium' | 'low' | null) {
    return apiClient.patch<{ success: boolean; data: { id: string; priority: string | null } }>(
      `${API_ENDPOINTS.CONVERSATIONS}/${id}/priority`,
      { priority }
    );
  },

  /**
   * Get total unread counts per tab across ALL conversations (not just current page).
   * Note: apiClient unwraps the response so res.data = { requestedUnreadTotal, steppedInUnreadTotal }
   */
  async getTabUnreadCounts(businessAccountId: string) {
    return apiClient.get<{
      requestedUnreadTotal: number;
      steppedInUnreadTotal: number;
    }>(`${API_ENDPOINTS.CONVERSATIONS}/stats/tab-counts`, {
      params: {
        business_account_id: businessAccountId,
      },
    });
  },

  /**
   * Get high priority conversations count
   */
  async getHighPriorityCount(businessAccountId: string) {
    return apiClient.get<{
      success: boolean;
      data: {
        highPriorityCount: number;
      };
    }>(`${API_ENDPOINTS.CONVERSATIONS}/stats/high-priority`, {
      params: {
        business_account_id: businessAccountId,
      },
    });
  },
};
